import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { faLock, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { CalendarSvgIcon, PhoneSvgIcon, UsersSvgIcon, UserSvgIcon, ClockSvgIcon, BubbleTextSvgIcon, ZoneSvgIcon, NoPhoneSvgIcon, NoPhoneTwoLinesSvgIcon } from "../icons/SvgIcons";
import { CustomModal } from "./CustomModal";
import { DefaultDateSmall, DefaultDateSmallMins } from "./DefaultImports";
const StyledInput = styled.input`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  &:focus {
    outline: none;
  }
`;
const ChatButton = styled.button`
  position: relative;
  border: 0;
  background: ${({
  isClient
}) => isClient ? "#EE754C" : "none"};
  color: ${({
  $blocked,
  isClient
}) => $blocked ? "#EE754C" : isClient ? "#FFF" : "#99b0ad"};
  cursor: pointer;
  height: 40px;
  margin: 0;
  padding: ${({
  isClient
}) => isClient ? "0 12px" : "0"};
  border-radius: 5px;
  font-weight: ${({
  isClient
}) => isClient ? "bold" : "normal"};
  font-size: ${({
  isClient
}) => isClient ? "14px" : "inherit"};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-direction: row;
  box-shadow: ${({
  isClient
}) => isClient ? "0 2px 4px rgba(0,0,0,0.2)" : "none"};
  width: ${({
  isClient
}) => isClient ? "100%" : "auto"};
`;
const ChatLockedIcon = styled.span`
  position: absolute;
  top: 0;
  right: -2px;
  color: #ee754c;
  font-size: 13px;
  line-height: 1;
  filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.85));
`;
const UpgradeModalText = styled.p`
  margin: 0;
  color: #335c67;
`;
const UpgradeModalButton = styled.button`
  border: 0;
  border-radius: 6px;
  padding: 8px 14px;
  background: ${({
  $secondary
}) => $secondary ? "#99b0ad" : "#ee754c"};
  color: #fff;
  font-weight: 600;
`;
const NotificationIndicator = styled.div`
  position: absolute;
  top: 5px;
  right: 2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({
  $unseen
}) => $unseen ? "#EE754C" : "#FAF6E9"};
  border: ${({
  $unseen
}) => $unseen ? "none" : "1px solid rgba(0, 0, 0, 0.08)"};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;
const UpgradeModalHighlight = styled.span`
  color: #ee754c;
  font-weight: 700;
`;
function ReservationChat({
  reservation,
  isClient = false,
  setChatModalTrueOrFalse = false,
  openModal = false,
  hideButton = false
}) {
  const {
    t
  } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showChatUpgradeModal, setShowChatUpgradeModal] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const socket = useRef(null);
  const navigate = useNavigate();
  const userLogin = useSelector(state => state.userLogin);
  const {
    userInfo
  } = userLogin;
  const chatEndRef = useRef(null);
  const restaurant_info = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurant_info;
  const isChatBlocked = !isClient && [0, 1, 2, 5].includes(Number(restaurants?.sub_lvl));
  useEffect(() => {
    if (openModal === true) {
      if (isChatBlocked) {
        setShowChatUpgradeModal(true);
      } else {
        setShowModal(openModal);
      }
    }
  }, [openModal, isChatBlocked]);
  const handleChatButtonClick = () => {
    if (isChatBlocked) {
      setShowChatUpgradeModal(true);
      return;
    }
    setShowModal(true);
  };
  useEffect(() => {
    if (!showModal) return;
    const sender = userInfo ? userInfo.name : reservation.full_name;
    const websocketBaseURL = process.env.REACT_APP_WEBSOCKET_URL;
    socket.current = new WebSocket(`${websocketBaseURL}chat/init/${reservation.token}/${sender}/${isClient ? "client" : "restaurant"}/`);
    socket.current.onmessage = event => {
      const data = JSON.parse(event.data);
      if (data.type === "chat_history") {
        const sortedMessages = [...data.messages].sort((a, b) => {
          const dateA = new Date(a.timestamp);
          const dateB = new Date(b.timestamp);
          return dateA - dateB;
        });
        setChatHistory(sortedMessages);
      } else {
        setChatHistory(prevHistory => {
          const updatedHistory = [...prevHistory, data];
          return updatedHistory.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateA - dateB;
          });
        });
      }
    };
    return () => socket.current?.close();
  }, [userInfo, showModal, reservation, isClient]);
  const sendMessage = () => {
    if (socket.current && newMessage.trim()) {
      socket.current.send(JSON.stringify({
        message: newMessage,
        sender: userInfo ? userInfo.name : reservation.full_name,
        client_or_restaurant: isClient ? "client" : "restaurant"
      }));
      setNewMessage("");
    }
  };
  const isUserMessage = senderName => {
    if (userInfo && senderName === userInfo.name) return true;
    if (!userInfo && senderName === reservation.full_name) return true;
    return false;
  };
  const closeModal = () => {
    if (setChatModalTrueOrFalse) {
      setChatModalTrueOrFalse(false);
    }
    setShowModal(false);
  };
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      block: "end"
    });
  }, [chatHistory, showModal]);
  const notificationIsActive = reservation.chat_notification_restaurant && Array.isArray(reservation.chat_notification_restaurant) && reservation.chat_notification_restaurant.includes(userInfo && userInfo.name) || reservation.chat_notification_client && isClient;
  return <>
      {!hideButton && <ChatButton isClient={isClient} $blocked={isChatBlocked} onClick={handleChatButtonClick} aria-disabled={isChatBlocked}>
          {isClient && "Mensagens"}

          <BubbleTextSvgIcon width={isClient ? 30 : 43} height={isClient ? 30 : 43} strokeColor={isChatBlocked ? "#EE754C" : "#FAF6E9"} />

          {reservation.chat_has_messages && <NotificationIndicator $unseen={notificationIsActive} />}
        </ChatButton>}

      <ChatModal show={showModal} onHide={closeModal} centered title={<TitleWrapper>
            <IconGroup>
              <UserSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              <Truncate>{reservation.full_name}</Truncate>
            </IconGroup>

            <IconGroup>
              <CalendarSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              <DefaultDateSmall date={moment.tz(`${reservation.date}`, "YYYY-MM-DD", restaurants.timezone)} />
            </IconGroup>

            <IconGroup>
              <ClockSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              {reservation.time}
            </IconGroup>

            <IconGroup>
              <UsersSvgIcon width={24} height={24} strokeColor="#FAF6E9" />
              {reservation.number_of_people}
            </IconGroup>

            <IconGroup>
              <ZoneSvgIcon width={20} height={20} strokeColor="#FAF6E9" />
              {reservation.table_place_preference ? reservation.table_place_preference.place_of_table : t("ui.i18nCleanup2.naoDefinirSala")}
            </IconGroup>
          </TitleWrapper>} footer={<InputContainer>
            <StyledTextarea placeholder={t("ui.components.reservationChat.escreverMensagem")} value={newMessage} onChange={e => setNewMessage(e.target.value)} rows={1} />
            <SendButton onClick={sendMessage}>
              <FontAwesomeIcon icon={faPaperPlane} size="lg" color="#9FBBC1" />
            </SendButton>
          </InputContainer>}>
        <ChatBody>
          <ChatHistoryContainer>
            {chatHistory.map((msg, idx) => <React.Fragment key={idx}>
                <SenderName isUserMessage={isUserMessage(msg.sender)}>
                  <span className="sender">{msg.sender}</span>
                </SenderName>

                <TimestampCenter>
                  <span>
                    {msg.timestamp ? <DefaultDateSmallMins date={msg.timestamp} /> : t("ui.i18nFinalCleanup.todayLower")}
                  </span>
                </TimestampCenter>

                <MessageBubble className={isUserMessage(msg.sender) ? "user-message" : "other-message"}>
                  {msg.message.split("\n").map((line, i) => <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>)}
                </MessageBubble>
              </React.Fragment>)}
            <div ref={chatEndRef} />
          </ChatHistoryContainer>
        </ChatBody>
      </ChatModal>

      <CustomModal show={showChatUpgradeModal} onHide={() => setShowChatUpgradeModal(false)} centered title={t("ui.components.reservationChat.chatSoup")} footer={<>
            <UpgradeModalButton type="button" $secondary onClick={() => setShowChatUpgradeModal(false)}>{t("ui.components.reservationChat.fechar")}</UpgradeModalButton>
            <UpgradeModalButton type="button" onClick={() => {
        setShowChatUpgradeModal(false);
        navigate(`/pick_plan_and_pay/${restaurants?.id}`);
      }}>{t("ui.components.reservationChat.verPlanos")}</UpgradeModalButton>
          </>}>
        <UpgradeModalText>{t("ui.components.reservationChat.oChatSoupEstaDisponivelNo")}{" "}
          <UpgradeModalHighlight>{t("ui.components.reservationChat.completo")}</UpgradeModalHighlight>.
        </UpgradeModalText>
      </CustomModal>
    </>;
}
export default ReservationChat;
const TitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.2;
  color: #faf6e9;
  margin-right: 36px;

  @media (max-width: 576px) {
    font-size: 11px;
    gap: 4px;
    margin-right: 28px;
  }
`;
const IconGroup = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    @media (max-width: 576px) {
      width: 16px !important;
      height: 16px !important;
    }
  }
`;
const Truncate = styled.span`
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 576px) {
    max-width: 70px;
  }
`;
const ChatModal = styled(CustomModal)`
  .modal-dialog {
    max-width: 500px;
    width: 90%;
    min-height: unset;
  }

  .modal-content {
    height: 700px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    flex: 0 0 auto;

    @media (max-width: 576px) {
      padding: 6px 12px;
    }
  }

  .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .modal-footer {
    flex: 0 0 auto;
    display: block;
    padding: 0;
  }

  @media (max-width: 576px) {
    .modal-dialog {
      width: 95%;
      margin: 0 auto;
    }

    .modal-content {
      height: 80vh;
    }
  }
`;
const ChatBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
const ChatHistoryContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;
const MessageBubble = styled.div`
  max-width: 80%;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 20px;
  color: #fff;
  word-break: break-word;

  &.user-message {
    background-color: #ee754c;
    margin-left: auto;
    border-bottom-right-radius: 0;
  }

  &.other-message {
    background-color: #99b0ad;
    margin-right: auto;
    border-bottom-left-radius: 0;
  }
`;
const SenderName = styled.div`
  margin-bottom: 5px;
  text-align: ${({
  isUserMessage
}) => isUserMessage ? "right" : "left"};
  color: #6c757d;
  font-size: 0.9rem;
`;
const TimestampCenter = styled.div`
  text-align: center;
  font-size: 0.8rem;
  margin-bottom: 5px;
  color: #6c757d;
`;
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border-top: 1px solid #ced4da;
  background: #fff;
`;
const StyledTextarea = styled.textarea`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  resize: none;
  max-height: 120px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &:focus {
    outline: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 10px;
  }
`;
const SendButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 15px;
  flex: 0 0 auto;
`;
